/* utils/lrs.ts ────────────────────────────────────────────────────── */
import axios from 'axios';

/* ------------------------------------------------------------------ */
/*  Pick API host                                                     */
/* ------------------------------------------------------------------ */
const API_HOST =
  process.env.REACT_APP_LRS_HOST ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:8000'         // dev PHP host
    : 'https://www.yisumatica.org.il');

const ENDPOINT = `${API_HOST}/yisumaticaApiZvi/recordLrsStatementLive.php`;

/* ------------------------------------------------------------------ */
/*  Shared axios instance                                             */
/* ------------------------------------------------------------------ */
const http = axios.create({
  timeout : 5000,
  headers : { 'Content-Type': 'application/json' }
});

/* ------------------------------------------------------------------ */
/*  Generic full‑statement sender                                     */
/* ------------------------------------------------------------------ */
export async function sendLrs(
  payload: Record<string, unknown>,   // already a complete xAPI object
  verb: string,
  appId = 'yisumatica'
) {
  const hostname = window.location.hostname;
  const isLocalHost =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    hostname.endsWith('.local');
  const isProdBuild = process.env.NODE_ENV === 'production';

  if (!isProdBuild || isLocalHost) {
    console.info('[LRS] Skipping sendLrs (environment not production or running on localhost)', {
      hostname,
      isProdBuild
    });
    return;
  }

  try {
    console.log('[LRS] POST full statement', { verb, appId, payload });
    await http.post(ENDPOINT, {
      statement: JSON.stringify(payload),   // PHP expects string
      verb,
      appId
    });
  } catch (err) {
    console.error('[LRS] failed – non‑blocking', err);
  }
}

/* ------------------------------------------------------------------ */
/*  Helper: Determine MoE identity type from userId                   */
/* ------------------------------------------------------------------ */
const getIdentityHomePage = (userId: string): string => {
  // If userId is numeric (only digits), use idnumber; otherwise exidentifier
  const isNumericId = /^\d+$/.test(userId);
  const homePage = isNumericId
    ? 'https://lxp.education.gov.il/xapi/moe/identity/idnumber'
    : 'https://lxp.education.gov.il/xapi/moe/identity/exidentifier';
  return homePage;
};

/* ------------------------------------------------------------------ */
/*  Helper: Hash identity number for MoE (SHA-256)                    */
/* ------------------------------------------------------------------ */
const hashIdentityNumber = async (value: string): Promise<string> => {
  // Always hash the value (whether it's an ID number or email) for MoE compliance
  // MoE requires hashed identity, not plain text
  const encoder = new TextEncoder();
  const data = encoder.encode(value);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
};

/* ------------------------------------------------------------------ */
/*  Lightweight login / logout pings – MoE verbs + LMS activity       */
/* ------------------------------------------------------------------ */
const ping = async (
  verb: 'login' | 'logout',
  userId: string,
  semelMosad = ''
) => {
  const hostname = window.location.hostname;
  const isLocalHost =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    hostname.endsWith('.local');
  const isProdBuild = process.env.NODE_ENV === 'production';

  if (!isProdBuild || isLocalHost) {
    console.info(`[LRS] ${verb.toUpperCase()} - Skipping MoE ping (environment not production or running on localhost)`, {
      hostname,
      isProdBuild
    });
    return;
  }

  try {
    console.group(`[LRS] ${verb.toUpperCase()} - Building MoE statement`);
    console.log('[LRS] ========================================');
    console.log('[LRS] MoE Compliance Check');
    console.log('[LRS] ========================================');
    
    const userName = localStorage.getItem('userName') ?? userId;
    console.log('[LRS] User info:', { userId, userName, semelMosad });

    // MoE verbs
    const verbId = verb === 'login'
      ? 'https://lxp.education.gov.il/xapi/moe/verbs/enter'
      : 'https://lxp.education.gov.il/xapi/moe/verbs/exit';

    // LMS activity (the LMS itself)
    const lmsId  = window.location.origin; // e.g. https://www.yisumatica.org.il
    const lmsTyp = 'https://lxp.education.gov.il/xapi/moe/lms'; // Fixed: removed /activities/

    // Determine identity type
    const identityHomePage = getIdentityHomePage(userId);
    const identityType = /^\d+$/.test(userId) ? 'idnumber' : 'exidentifier';

    // Hash identity number if it's numeric (Israeli ID number)
    const hashedIdentity = await hashIdentityNumber(userId);

    console.log('[LRS] ========================================');
    console.log('[LRS] MoE Compliance Details:');
    console.log('[LRS]   ✓ Verb ID:', verbId);
    console.log('[LRS]   ✓ LMS ID:', lmsId);
    console.log('[LRS]   ✓ LMS activity type:', lmsTyp, '(should be /moe/lms, NOT /activities/lms)');
    console.log('[LRS]   ✓ Identity type:', identityType);
    console.log('[LRS]   ✓ Actor homePage:', identityHomePage);
    console.log('[LRS]   ✓ Hashed identity:', hashedIdentity);
    console.log('[LRS] ========================================');

    // Optional session duration (seconds) → ISO-8601 PT…S on logout
    let result: any | undefined;
    if (verb === 'logout') {
      const sec = Number(localStorage.getItem('sessionDuration') ?? 0);
      console.log('[LRS] Session duration from localStorage:', sec, 'seconds');
      if (!Number.isNaN(sec) && sec > 0) {
        const h = Math.floor(sec / 3600);
        const m = Math.floor((sec % 3600) / 60);
        const s = Math.floor(sec % 60);
        const dur = `PT${h ? `${h}H` : ''}${(h || m) ? `${m}M` : ''}${s}S`;
        result = { duration: dur };
        console.log('[LRS] Session duration converted to ISO-8601:', dur);
      } else {
        console.warn('[LRS] Missing sessionDuration – result.duration will be omitted');
      }
    }

    const traceId = `lrs-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const statement = {
      actor: {
        objectType: 'Agent',
        account: { 
          homePage: identityHomePage, // Fixed: MoE identity (idnumber or exidentifier)
          name: hashedIdentity // Fixed: hashed identity number instead of email
        }
      },
      verb: { id: verbId },
      object: {
        objectType: 'Activity',
        id: lmsId,
        definition: {
          type: lmsTyp, // Fixed: /moe/lms instead of /moe/activities/lms
          name: { he: 'Yisumatica LMS' },
          description: { he: 'מערכת למידה Yisumatica' } // Fixed: describes the system, not the action
        }
      },
      context: {
        contextActivities: {
          grouping: [
            {
              objectType: 'Activity',
              id: lmsId,
              definition: {
                type: lmsTyp, // Fixed: added definition.type
                name: { he: 'Yisumatica LMS' }
              }
            }
          ]
        },
        extensions: {
          'https://yisumatica.org.il/xapi/extensions/traceId': traceId
        }
      },
      ...(result ? { result } : {}),
      timestamp: new Date().toISOString(),
      version: '1.0.3'
    };

    console.log('[LRS] ========================================');
    console.log('[LRS] Full statement object (MoE-compliant):');
    console.log(JSON.stringify(statement, null, 2));
    console.log('[LRS] ========================================');
    console.log('[LRS] Trace ID:', traceId);

    const payload = {
      statement: JSON.stringify([statement]),
      verb: verb === 'login' ? 'enter' : 'exit',
      appId: 'lms-shell',
      userName,
      semelMosad,
      userId,
      traceId
    };

    console.log('[LRS] ========================================');
    console.log('[LRS] Payload to PHP:');
    console.log('[LRS]   verb:', payload.verb);
    console.log('[LRS]   appId:', payload.appId);
    console.log('[LRS]   userName:', payload.userName);
    console.log('[LRS]   statementLength:', payload.statement.length);
    console.log('[LRS]   statementPreview:', payload.statement.substring(0, 300) + '...');
    console.log('[LRS]   traceId:', traceId);
    console.log('[LRS] ========================================');

    // For logout, prefer beacon (survives navigation). Fallback to fetch keepalive, then axios.
    if (verb === 'logout') {
      const body = JSON.stringify(payload);
      try {
        if (navigator.sendBeacon?.(ENDPOINT, body)) {
          console.log('[LRS] ✓ LOGOUT sent via sendBeacon', { traceId });
          console.log('[LRS] ✓ Ministry will receive MoE-compliant statement');
        } else {
          console.log('[LRS] sendBeacon unavailable, trying fetch keepalive');
          const r = await fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true
          });
          console.log('[LRS] ✓ LOGOUT fetch keepalive status:', r.status, { traceId });
          console.log('[LRS] ✓ Ministry will receive MoE-compliant statement');
        }
      } catch (e) {
        console.warn('[LRS] LOGOUT beacon/fetch failed, falling back to axios', e);
        const response = await http.post(ENDPOINT, payload);
        console.log(`[LRS] ${verb.toUpperCase()} ✓ Success - Response:`, response.data, { traceId });
        console.log('[LRS] ✓ Ministry will receive MoE-compliant statement');
      }
    } else {
      const response = await http.post(ENDPOINT, payload);
      console.log(`[LRS] ${verb.toUpperCase()} ✓ Success - Response:`, response.data, { traceId });
      console.log('[LRS] ✓ Ministry will receive MoE-compliant statement');
    }
    console.groupEnd();
  } catch (err) {
    console.error(`[LRS] ${verb.toUpperCase()} ✗ Failed:`, err);
    console.groupEnd();
  }
};

export const pingLogin  = (uid: string, semel = '') => ping('login',  uid, semel);
export const pingLogout = (uid: string, semel = '') => ping('logout', uid, semel);
